import { AxiosResponse } from 'axios';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';
import {
  UploadReviewResponseDto,
  RunJobRequestDto,
  JobOperationResponseDto,
  JobStatusResponseDto,
  ReviewOperationRequestDto,
  JobDetailsResponseDto,
  ReviewListResponseDto,
  JobListResponseDto
} from './bulkPricingReview.types';

// Create an axios instance for direct S3 API calls
const s3ApiClient = axios.create({
  // In development, use proxy (no baseURL); in production, use direct S3 API URL
  baseURL: process.env.NODE_ENV === 'production' ? process.env.REACT_APP_OPSUI_API_GO_URL : '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor to S3 API client
s3ApiClient.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

// Upload file for review - goes to moa-api at 9011
export const useUploadReviewFile = () => {
  return useMutation({
    mutationFn: async (params: { file: File; userId: string }): Promise<UploadReviewResponseDto> => {
      const { file, userId } = params;
      
      // Validate file type - only CSV files are allowed
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        throw new Error('Only CSV files are allowed');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<UploadReviewResponseDto>(
        '/bulk-pricing/review/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-User-Id': userId, // REQUIRED header
          },
        }
      );

      return response.data;
    },
  });
};
export const useRunJob = () => {
  return useMutation<AxiosResponse<JobOperationResponseDto>, Error, RunJobRequestDto>((requestBody) => {
    const { userId, operations, ...params } = requestBody;
    return apiClient.post<JobOperationResponseDto>(
      '/bulk-pricing/review/run-job',
      operations ?? null,
      { headers: { 'X-User-Id': userId }, params }
    );
  });
};

// Get job status - uses apiClient (moa-api for DB operations)
export const useJobStatus = (fileId?: number, fileName?: string, franchiseCode?: string, enabled: boolean = false) => {
  return useQuery<AxiosResponse<JobStatusResponseDto>>({
    queryKey: ['job-status', fileId, fileName, franchiseCode],
    queryFn: () => {
      const params: any = {};
      if (fileId) params.fileId = fileId;
      if (fileName) params.fileName = fileName;
      if (franchiseCode) params.franchiseCode = franchiseCode;
      return apiClient.get<JobStatusResponseDto>('/bulk-pricing/review/job-status', { params });
    },
    enabled: enabled && !!(fileId || (fileName && franchiseCode)),
    refetchInterval: (data) => {
      const status = data?.data?.status;
      return status === 'IN_PROGRESS' ? 5000 : false;
    },
  });
};

// Get job details (including individual jobs)
export const useJobDetails = (reviewId: number | undefined, enabled: boolean = false) => {
  return useQuery<AxiosResponse<JobDetailsResponseDto>>({
    queryKey: ['review-details', reviewId],
    queryFn: () => apiClient.get<JobDetailsResponseDto>(`/bulk-pricing/review/details/${reviewId}`),
    enabled: enabled && reviewId !== undefined,
  });
};

// List jobs for franchise - uses apiClient (Operations-UI API for DB operations)
export const useListJobs = (
  franchiseCode: string,
  status?: string,
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<JobListResponseDto>>({
    queryKey: ['list-jobs', franchiseCode, status],
    queryFn: () => {
      const params: any = { franchiseCode };
      if (status) params.status = status;
      return apiClient.get<JobListResponseDto>('/bulk-pricing/review/list-jobs', { params });
    },
    enabled: enabled && !!franchiseCode,
  });
};

// List all review objects from S3 bucket - always uses proxiedAxios (goes to S3 API)
export const useReviewList = (
  franchiseCode?: string,
  status?: string,
  enabled: boolean = true
) => {
  return useQuery<AxiosResponse<ReviewListResponseDto>>({
    queryKey: ['review-list', franchiseCode, status],
    queryFn: () => {
      const params: any = {};
      if (franchiseCode) {
        params.franchiseCode = franchiseCode;
      }
      if (status) {
        params.status = status;
      }      // Use s3ApiClient for direct S3 operations
      return s3ApiClient.get<ReviewListResponseDto>('/api/review/list', { params });
    },
    enabled,
  });
};

// List S3 objects directly - uses proxiedAxios (moa-yum-api port 9014)
export const useS3ReviewList = (
  franchiseCode?: string,
  status?: string,
  enabled: boolean = true
) => {
  return useQuery<AxiosResponse<ReviewListResponseDto>>({
    queryKey: ['s3-review-list', franchiseCode, status],
    queryFn: () => {
      const params: any = {};
      if (franchiseCode) {
        params.franchiseCode = franchiseCode;
      }
      if (status) {
        params.status = status;
      }      // Use s3ApiClient for direct S3 operations
      return s3ApiClient.get<ReviewListResponseDto>('/api/review/list', { params });
    },
    enabled,
  });
};

// Archive review - uses apiClient (moa-api handles DB + calls S3 API)
export const useArchiveReview = () => {
  return useMutation<AxiosResponse<JobOperationResponseDto>, Error, ReviewOperationRequestDto & { userId?: string }>(async (requestBody) => {
    const { userId, ...archiveData } = requestBody;
    
    const config: any = {};
    if (userId) {
      config.headers = { 'X-User-Id': userId };
    }
    
    return apiClient.post<JobOperationResponseDto>('/bulk-pricing/review/archive', archiveData, config);
  });
};

// Cancel review - uses apiClient (moa-api handles DB + calls S3 API)
export const useCancelReview = () => {
  return useMutation<AxiosResponse<JobOperationResponseDto>, Error, ReviewOperationRequestDto & { userId?: string }>(async (requestBody) => {
    console.log('Cancel review request body:', requestBody);
    const { userId, ...cancelData } = requestBody;
    
    const config: any = {};
    if (userId) {
      config.headers = { 'X-User-Id': userId };
    }
    
    return apiClient.post<JobOperationResponseDto>('/bulk-pricing/review/cancel', cancelData, config);
  });
};

// Fail review - uses apiClient (moa-api handles DB + calls S3 API)
export const useFailReview = () => {
  return useMutation<AxiosResponse<JobOperationResponseDto>, Error, ReviewOperationRequestDto & { userId?: string }>(async (requestBody) => {
    const { userId, ...failData } = requestBody;
    
    const config: any = {};
    if (userId) {
      config.headers = { 'X-User-Id': userId };
    }
    
    return apiClient.post<JobOperationResponseDto>('/bulk-pricing/review/fail', failData, config);
  });
};

// Download review content - goes directly to S3 API (no DB records)
export const useDownloadReview = () => {
  return useMutation<AxiosResponse<any>, Error, { filePath?: string; fileId?: number; fileName?: string; franchiseCode?: string }>(async (requestBody) => {
    // Prefer the review's actual S3 key when provided — it is valid for both pending
    // (`moa-bulk-review/{franchise}/{file}`) and status-prefixed archived/canceled/failed
    // (`moa-bulk-review/{status}/{franchise}/{file}`) shapes. Fall back to reconstruction.
    let filePath: string;
    if (requestBody.filePath) {
      filePath = requestBody.filePath;
    } else if (requestBody.fileName && requestBody.franchiseCode) {
      filePath = `moa-bulk-review/${requestBody.franchiseCode}/${requestBody.fileName}`;
    } else if (requestBody.fileId) {
      // If only fileId is provided, we might need to construct the path differently
      filePath = `moa-bulk-review/${requestBody.fileId}`;
    } else {
      throw new Error('Either filePath, fileName+franchiseCode, or fileId must be provided');    }

    // Send the request body with FilePath as expected by the S3 API
    const response = await s3ApiClient.post<any>('/api/review/download', 
      { FilePath: filePath },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        // Explicitly ensure no query params are added
        params: undefined
      }
    );
    
    return response;
  });
};

// Utility function to validate bulk operations CSV file
export const validateBulkOperationsCsvFile = (file: File): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    // Strict CSV file validation - only CSV files accepted
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      reject(new Error('Only CSV files are allowed'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      reject(new Error('File size must be less than 10MB'));
      return;
    }

    // Basic CSV validation - just check that it has content and basic structure
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        
        if (!content || content.trim().length === 0) {
          reject(new Error('CSV file appears to be empty'));
          return;
        }

        const lines = content.trim().split('\n');
        if (lines.length < 2) {
          reject(new Error('CSV file must contain at least a header row and one data row'));
          return;
        }

        // Basic structure check - ensure we have some commas indicating CSV format
        const headerRow = lines[0];
        if (!headerRow.includes(',')) {
          reject(new Error('File does not appear to be in valid CSV format'));
          return;
        }

        resolve(true);
      } catch (error) {
        reject(new Error('Failed to validate CSV file format'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

// Get status badge color for UI
export const getStatusBadgeColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'uploaded':
      return 'info'; // New initial status after file upload
    case 'pending':
      return 'warning';
    case 'processing':
    case 'in_progress':
      return 'info';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'canceled':
    case 'cancelled':
      return 'error';
    case 'archived':
      return 'default';
    default:
      return 'default';
  }
};

// Calculate progress percentage
export const calculateProgress = (completedJobs: number, totalJobs: number): number => {
  if (totalJobs === 0) return 0;
  return Math.round((completedJobs / totalJobs) * 100);
};
